import { type ChangeEvent, type FocusEvent, useId, useCallback } from "react";
import type { IB2BTextFieldProps } from "@hopper-b2b/ui";
import { useInputFocusHandling } from "@hopper-b2b/utilities";
import clsx from "clsx";
import "./NativeInput.scss";

interface NativeInputProps extends IB2BTextFieldProps {
  defaultValue?: unknown;
  value?: unknown;
  autoCapitalize?: "off" | "none" | "on" | "sentences" | "words" | "characters";
}

/**
 * Native HTML input that bypasses MUI and CustomInput.
 *
 * Key differences from B2BTextField/CustomInput:
 * - NO onPointerDown handler with requestAnimationFrame
 * - NO manual focus() calls
 * - NO state updates during focus events
 * - Focus handling behavior controlled by inputFocusHandling feature flag
 * - Lets iOS Safari handle focus natively by default
 *
 * This component is designed to work reliably on iOS Safari in iframe/webview
 * contexts where the standard TextField components cause focus/scroll issues.
 */
export const NativeInput = ({
  onChange,
  onFocus,
  onBlur,
  error,
  helperText,
  className,
  id: providedId,
  label,
  required,
  value,
  defaultValue,
  placeholder,
  type,
  inputProps,
  disabled,
  autoCapitalize = "words",
  ...rest
}: NativeInputProps) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const inputFocusHandling = useInputFocusHandling();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur?.(event.target.value);
  };

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const target = event.target;

      // Call the onFocus handler first
      onFocus?.(target.value, event);

      // Apply focus handling based on feature flag
      switch (inputFocusHandling) {
        case "scroll-into-view":
          // Immediate scrollIntoView
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        case "delayed-scroll":
          // Delayed scrollIntoView (after iOS keyboard appears ~300ms)
          setTimeout(() => {
            target.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 300);
          break;
        case "android-only": {
          // Only scroll on Android - iOS handles it natively
          const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
          if (!isIOS) {
            target.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          break;
        }
        case "control":
        default:
          // No scroll intervention - let browser handle natively
          break;
      }
    },
    [onFocus, inputFocusHandling]
  );

  return (
    <div
      className={clsx(
        "native-input-root",
        { "native-input-error": error, required },
        className
      )}
    >
      {label && (
        <label htmlFor={id} className="native-input-label">
          {label}
          {required ? "*" : ""}
        </label>
      )}
      <input
        id={id}
        className="native-input-field"
        type={type || "text"}
        value={value as string | number | undefined}
        defaultValue={defaultValue as string | number | undefined}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-invalid={error}
        aria-describedby={helperText ? `${id}-helper` : undefined}
        spellCheck={false}
        autoCapitalize={autoCapitalize}
        autoCorrect="off"
        {...inputProps}
      />
      {helperText && (
        <p id={`${id}-helper`} className="native-input-helper">
          {helperText}
        </p>
      )}
    </div>
  );
};
